import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import useApiData from "../../services/useApiData.js";
import PostData from "../../services/PostData.js";
import PutData from "../../services/PutData.js";
import api from "../../services/axiosInterceptor.js";
import GetData from "../../services/GetData.js";
import {
    SingleSelectField,
    DimensionMultiSelect,
    DimensionSingleSelect,
    LookupField,
    SkuTextField,
} from "./TypedFields.jsx";
import SelectOptionsDialog from "./SelectOptionsDialog.jsx";
import {
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
} from "./catalogGridStyles.js";
import AuthenticatedAssetImage from "../Helpers/AuthenticatedAssetImage.jsx";

const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

function revokeUrl(url) {
    if (url && String(url).startsWith("blob:")) {
        URL.revokeObjectURL(url);
    }
}

export const EMPTY_PRODUCT_FORM = {
    itemName: "",
    internalMoniker: "",
    azCategory: "",
    ebayCategory: "",
    gender: "",
    conditionGrade: "",
    packagingType: "",
    partialPackEligible: "",
    ebayShippingPolicy: "",
    ebayDescriptionTemplate: "",
    descriptionTemplateValueEbayConfig: "",
    ebayConditionField: "",
    ebayPublicInterfaceView: "",
    walmartBlocked: "",
    brand: [],
    size: [],
    colorFlavor: [],
    model: [],
    statusChecks: [],
    upc: "",
    gtin: "",
    asin: "",
    amazonUrl: "",
    walmartUrl: "",
    totalQuantity: "",
    innerPacks: "",
    viablePackCount: "",
    weightLbs: "",
    heightIn: "",
    widthIn: "",
    depthIn: "",
    msrpUsd: "",
    velocityAmazonSalesPerMonth: "",
    vistaMaxPrice: "",
    walmartPrice: "",
    ebayPrice: "",
    walmartLinkedSku: "",
    ebayLinkedSku: "",
    volumePricing2x: "",
    volumePricing3x: "",
    volumePricing4x: "",
    inspectionWizardBuiltSku: false,
    notes: "",
};

const EMPTY_COMPUTED = {
    listingField: "",
    internalSkuId: "",
    wpid: "",
    macMaxPrice: "",
    bidftaMaxPrice: "",
    ffPricing: "",
    fbMktPricing: "",
    purchasePriceEbay: "",
    estShippingCost: "",
    costRollup1x: "",
    costRollup2x: "",
    costRollup3x: "",
    costRollup4x: "",
    suggestedEbayPrice: "",
    suggestedWalmartPrice: "",
    suggestedMargin: "",
    vistaPackPrice: "",
    helperFlagWalmartAutomation: "",
    listedPlatforms: "",
    quantitySyncErrors: [],
    availableQuantityEbay: "",
    availableQuantityWalmart: "",
    ebayQuant30PlusDays: "",
    walmartQuant30PlusDays: "",
    ebayOlderListingAge: "",
    walmartOlderListingAge: "",
    totalQuantOnHand: "",
    oldestPurchaseDate: "",
    expirationDateRollup: "",
    positiveExampleCount: "",
    negativeExampleCount: "",
};

const EMPTY_DIMENSION_SEEDS = {
    brand: [],
    size: [],
    colorFlavor: [],
    model: [],
    statusChecks: [],
};

function toNumberOrNull(value) {
    if (value === "" || value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function moneyText(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function countOf(value) {
    return Array.isArray(value) ? value.length : 0;
}

function idList(arr) {
    return (arr || []).map((entry) => (typeof entry === "object" ? entry._id : entry)).filter(Boolean);
}

function linkRecords(arr) {
    return (arr || [])
        .filter((entry) => entry && typeof entry === "object" && entry._id)
        .map((entry) => ({
            _id: entry._id,
            name: entry.name || "",
            cipher: entry.cipher || "",
            code: entry.code || "",
        }));
}

function applyCreateSeeds(seeds) {
    if (!seeds || typeof seeds !== "object") return { ...EMPTY_PRODUCT_FORM };
    return { ...EMPTY_PRODUCT_FORM, ...seeds };
}

/** Match server asinFromAmazonUrl — /dp/{10-char ASIN}. */
function extractAsinFromAmazonUrl(url) {
    const text = String(url || "").trim();
    if (!text || /^n\/a$/i.test(text)) return "";
    const match = text.match(/\/dp\/([A-Za-z0-9]{10})/i);
    return match ? match[1].toUpperCase() : "";
}

/**
 * Full Product create/edit dialog shared by Products grid and CSV unmatched flow.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string|null} [props.productId] — set for edit; null/undefined for create
 * @param {object} [props.createSeeds] — partial form overrides when creating
 * @param {(doc: object) => void} [props.onSaved]
 * @param {(snack: { color: string, message: string }) => void} [props.onNotify]
 */
export default function ProductFormDialog({
    open,
    onClose,
    productId = null,
    createSeeds = null,
    onSaved,
    onNotify,
}) {
    const editingId = productId || null;
    const [form, setForm] = useState(EMPTY_PRODUCT_FORM);
    const [dimensionSeeds, setDimensionSeeds] = useState(EMPTY_DIMENSION_SEEDS);
    const [computed, setComputed] = useState(EMPTY_COMPUTED);
    const [heroPreview, setHeroPreview] = useState(null);
    const [heroFile, setHeroFile] = useState(null);
    const [heroLocalUrl, setHeroLocalUrl] = useState("");
    const [supportingFiles, setSupportingFiles] = useState([]);
    const [existingSupporting, setExistingSupporting] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loadingEdit, setLoadingEdit] = useState(false);
    const [optionEditor, setOptionEditor] = useState(null);
    const heroInputRef = useRef(null);
    const supportingInputRef = useRef(null);
    const loadSeq = useRef(0);

    const { data: fieldMeta, refetch: refetchFieldMeta } = useApiData(
        "api/broylesllc/catalog-field-meta"
    );
    const productMeta = fieldMeta?.product || {};

    const notify = (color, message) => {
        if (onNotify) onNotify({ color, message });
    };

    const clearLocalPhotos = () => {
        revokeUrl(heroLocalUrl);
        supportingFiles.forEach((entry) => revokeUrl(entry.url));
        setHeroFile(null);
        setHeroLocalUrl("");
        setSupportingFiles([]);
    };

    useEffect(() => {
        return () => {
            revokeUrl(heroLocalUrl);
            supportingFiles.forEach((entry) => revokeUrl(entry.url));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- revoke only on unmount
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const seq = ++loadSeq.current;
        const seedsSnapshot = createSeeds;

        const resetCreate = () => {
            setForm(applyCreateSeeds(seedsSnapshot));
            setDimensionSeeds(EMPTY_DIMENSION_SEEDS);
            setComputed(EMPTY_COMPUTED);
            setHeroPreview(null);
            setExistingSupporting([]);
            clearLocalPhotos();
            setLoadingEdit(false);
        };

        if (!editingId) {
            resetCreate();
            return undefined;
        }

        let cancelled = false;
        setLoadingEdit(true);
        (async () => {
            const { getData } = await GetData(`broylesllc/products/${editingId}`);
            if (cancelled || seq !== loadSeq.current) return;
            if (!getData) {
                notify("error", "Could not load product.");
                setLoadingEdit(false);
                onClose?.();
                return;
            }
            const p = getData;
            const asText = (value) => (value == null || value === "" ? "" : String(value));
            setDimensionSeeds({
                brand: linkRecords(p.brand),
                size: linkRecords(p.size),
                colorFlavor: linkRecords(p.colorFlavor),
                model: linkRecords(p.model),
                statusChecks: linkRecords(p.statusChecks),
            });
            setForm({
                itemName: p.itemName || "",
                internalMoniker: p.internalMoniker || "",
                azCategory: p.azCategory || "",
                ebayCategory: p.ebayCategory || "",
                gender: p.gender || "",
                conditionGrade: p.conditionGrade || "",
                packagingType: p.packagingType || "",
                partialPackEligible: p.partialPackEligible || "",
                ebayShippingPolicy: p.ebayShippingPolicy || "",
                ebayDescriptionTemplate: p.ebayDescriptionTemplate || "",
                descriptionTemplateValueEbayConfig: p.descriptionTemplateValueEbayConfig || "",
                ebayConditionField: p.ebayConditionField || "",
                ebayPublicInterfaceView: p.ebayPublicInterfaceView || "",
                walmartBlocked: p.walmartBlocked || "",
                brand: idList(p.brand),
                size: idList(p.size),
                colorFlavor: idList(p.colorFlavor),
                model: idList(p.model),
                statusChecks: idList(p.statusChecks),
                upc: p.upc || "",
                gtin: p.gtin || "",
                asin: p.asin || "",
                amazonUrl: p.amazonUrl || "",
                walmartUrl: p.walmartUrl || "",
                totalQuantity: asText(p.totalQuantity),
                innerPacks: asText(p.innerPacks),
                viablePackCount: asText(p.viablePackCount),
                weightLbs: asText(p.weightLbs),
                heightIn: asText(p.heightIn),
                widthIn: asText(p.widthIn),
                depthIn: asText(p.depthIn),
                msrpUsd: asText(p.msrpUsd),
                velocityAmazonSalesPerMonth: asText(p.velocityAmazonSalesPerMonth),
                vistaMaxPrice: asText(p.vistaMaxPrice),
                walmartPrice: asText(p.walmartPrice),
                ebayPrice: asText(p.ebayPrice),
                walmartLinkedSku: p.walmartLinkedSku || "",
                ebayLinkedSku: p.ebayLinkedSku || "",
                volumePricing2x: asText(p.volumePricing2x),
                volumePricing3x: asText(p.volumePricing3x),
                volumePricing4x: asText(p.volumePricing4x),
                inspectionWizardBuiltSku: Boolean(p.inspectionWizardBuiltSku),
                notes: p.notes || "",
            });
            setComputed({
                listingField: p.listingField || "",
                internalSkuId: p.internalSkuId || "",
                wpid: p.wpid || "",
                macMaxPrice: moneyText(p.macMaxPrice),
                bidftaMaxPrice: moneyText(p.bidftaMaxPrice),
                ffPricing: moneyText(p.ffPricing),
                fbMktPricing: moneyText(p.fbMktPricing),
                purchasePriceEbay: moneyText(p.purchasePriceEbay),
                estShippingCost: moneyText(p.estShippingCost),
                costRollup1x: moneyText(p.costRollup1x),
                costRollup2x: moneyText(p.costRollup2x),
                costRollup3x: moneyText(p.costRollup3x),
                costRollup4x: moneyText(p.costRollup4x),
                suggestedEbayPrice: moneyText(p.suggestedEbayPrice),
                suggestedWalmartPrice: moneyText(p.suggestedWalmartPrice),
                suggestedMargin: moneyText(p.suggestedMargin),
                vistaPackPrice: moneyText(p.vistaPackPrice),
                helperFlagWalmartAutomation: p.helperFlagWalmartAutomation || "",
                listedPlatforms: Array.isArray(p.listedPlatforms)
                    ? p.listedPlatforms.join(", ")
                    : "",
                ebayNeedsMigrate:
                    Array.isArray(p.listedPlatforms) &&
                    p.listedPlatforms.includes("eBay") &&
                    !String(p.ebayOfferId || "").trim()
                        ? "Needs migrate"
                        : "",
                quantitySyncErrors: Array.isArray(p.quantitySyncErrors)
                    ? p.quantitySyncErrors.filter((e) => e && e.error !== false)
                    : [],
                availableQuantityEbay: asText(p.availableQuantityEbay),
                availableQuantityWalmart: asText(p.availableQuantityWalmart),
                ebayQuant30PlusDays: asText(p.ebayQuant30PlusDays),
                walmartQuant30PlusDays: asText(p.walmartQuant30PlusDays),
                ebayOlderListingAge: asText(p.ebayOlderListingAge),
                walmartOlderListingAge: asText(p.walmartOlderListingAge),
                totalQuantOnHand: asText(p.totalQuantOnHand),
                oldestPurchaseDate: p.oldestPurchaseDate
                    ? new Date(p.oldestPurchaseDate).toLocaleDateString()
                    : "",
                expirationDateRollup: p.expirationDateRollup
                    ? new Date(p.expirationDateRollup).toLocaleDateString()
                    : "",
                positiveExampleCount: String(countOf(p.positiveExamples)),
                negativeExampleCount: String(countOf(p.negativeExamples)),
            });
            const hero = Array.isArray(p.heroPhoto)
                ? p.heroPhoto.find((asset) => asset?.gcsObject || asset?.sourceUrl)
                : null;
            setHeroPreview(hero || null);
            setExistingSupporting(
                (Array.isArray(p.supportingPhotos) ? p.supportingPhotos : []).filter(
                    (asset) => asset?.gcsObject || asset?.sourceUrl
                )
            );
            clearLocalPhotos();
            setLoadingEdit(false);
        })();

        return () => {
            cancelled = true;
        };
        // Seed snapshot is captured when open/editingId change — avoid reset while typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editingId]);

    const handleClose = () => {
        clearLocalPhotos();
        onClose?.();
    };

    const onHeroSelected = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        if (!String(file.type || "").startsWith("image/")) {
            notify("warning", "Hero photo must be an image.");
            return;
        }
        revokeUrl(heroLocalUrl);
        setHeroFile(file);
        setHeroLocalUrl(URL.createObjectURL(file));
    };

    const clearHeroSelection = () => {
        revokeUrl(heroLocalUrl);
        setHeroFile(null);
        setHeroLocalUrl("");
    };

    const onSupportingSelected = (event) => {
        const list = Array.from(event.target.files || []);
        event.target.value = "";
        if (!list.length) return;
        const images = list.filter((file) => String(file.type || "").startsWith("image/"));
        if (!images.length) {
            notify("warning", "Secondary photos must be images.");
            return;
        }
        setSupportingFiles((prev) => [
            ...prev,
            ...images.map((file) => ({
                key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
                file,
                url: URL.createObjectURL(file),
            })),
        ]);
    };

    const removeSupportingAt = (key) => {
        setSupportingFiles((prev) => {
            const next = [];
            for (const entry of prev) {
                if (entry.key === key) {
                    revokeUrl(entry.url);
                } else {
                    next.push(entry);
                }
            }
            return next;
        });
    };

    const setField = (key) => (e) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value }));

    const setAmazonUrl = (e) => {
        const amazonUrl = e.target.value;
        setForm((prev) => {
            const next = { ...prev, amazonUrl };
            const extracted = extractAsinFromAmazonUrl(amazonUrl);
            if (!extracted) return next;
            const prevExtract = extractAsinFromAmazonUrl(prev.amazonUrl);
            const currentAsin = String(prev.asin || "").trim();
            // Fill when empty, or when ASIN still mirrors the previous URL scrape (not a manual override).
            if (
                !currentAsin ||
                currentAsin.toUpperCase() === String(prevExtract || "").toUpperCase()
            ) {
                next.asin = extracted;
            }
            return next;
        });
    };

    const setSelect = (key) => (value) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const setSingleLink = (key) => (id) =>
        setForm((prev) => ({ ...prev, [key]: id ? [id] : [] }));

    const save = async () => {
        if (!form.itemName.trim()) {
            notify("error", "Item Name is required.");
            return;
        }
        setSaving(true);
        const payload = {
            itemName: form.itemName.trim(),
            internalMoniker: form.internalMoniker.trim(),
            azCategory: form.azCategory,
            ebayCategory: form.ebayCategory,
            gender: form.gender,
            conditionGrade: form.conditionGrade,
            packagingType: form.packagingType,
            partialPackEligible: form.partialPackEligible,
            ebayShippingPolicy: form.ebayShippingPolicy,
            ebayDescriptionTemplate: form.ebayDescriptionTemplate,
            descriptionTemplateValueEbayConfig: form.descriptionTemplateValueEbayConfig,
            ebayConditionField: form.ebayConditionField,
            ebayPublicInterfaceView: form.ebayPublicInterfaceView,
            walmartBlocked: form.walmartBlocked,
            brand: form.brand,
            size: form.size,
            colorFlavor: form.colorFlavor,
            model: form.model,
            statusChecks: form.statusChecks,
            upc: form.upc.trim(),
            gtin: form.gtin.trim(),
            asin: form.asin.trim(),
            amazonUrl: form.amazonUrl.trim(),
            walmartUrl: form.walmartUrl.trim(),
            totalQuantity: toNumberOrNull(form.totalQuantity),
            innerPacks: toNumberOrNull(form.innerPacks),
            viablePackCount: toNumberOrNull(form.viablePackCount),
            weightLbs: toNumberOrNull(form.weightLbs),
            heightIn: toNumberOrNull(form.heightIn),
            widthIn: toNumberOrNull(form.widthIn),
            depthIn: toNumberOrNull(form.depthIn),
            msrpUsd: toNumberOrNull(form.msrpUsd),
            velocityAmazonSalesPerMonth: toNumberOrNull(form.velocityAmazonSalesPerMonth),
            vistaMaxPrice: toNumberOrNull(form.vistaMaxPrice),
            volumePricing2x: toNumberOrNull(form.volumePricing2x),
            volumePricing3x: toNumberOrNull(form.volumePricing3x),
            volumePricing4x: toNumberOrNull(form.volumePricing4x),
            inspectionWizardBuiltSku: Boolean(form.inspectionWizardBuiltSku),
            notes: form.notes,
        };

        let result;
        if (editingId) {
            result = await PutData(payload, `broylesllc/products/${editingId}`);
        } else if (heroFile || supportingFiles.length) {
            const formData = new FormData();
            for (const [key, value] of Object.entries(payload)) {
                if (value == null) continue;
                if (Array.isArray(value) || typeof value === "object") {
                    formData.append(key, JSON.stringify(value));
                } else if (typeof value === "boolean") {
                    formData.append(key, value ? "true" : "false");
                } else {
                    formData.append(key, String(value));
                }
            }
            if (heroFile) formData.append("heroPhoto", heroFile, heroFile.name);
            for (const entry of supportingFiles) {
                formData.append("supportingPhotos", entry.file, entry.file.name);
            }
            try {
                const response = await api.post("api/broylesllc/products", formData);
                result = { success: true, data: response.data };
            } catch (err) {
                result = { success: false, error: err };
            }
        } else {
            result = await PostData(payload, "broylesllc/products");
        }

        if (!result.success) {
            setSaving(false);
            const msg =
                result?.data?.message ||
                result?.error?.response?.data?.message ||
                "Save failed.";
            notify("error", msg);
            return;
        }

        clearLocalPhotos();
        notify("success", editingId ? "Product updated." : "Product created.");
        try {
            // Await parent follow-up (e.g. link catalog items) before closing so
            // the caller still has dialog context / pending group data.
            if (onSaved) await Promise.resolve(onSaved(result.data));
        } catch (err) {
            notify(
                "error",
                err?.message || "Product saved, but follow-up action failed."
            );
        } finally {
            setSaving(false);
            onClose?.();
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: { borderRadius: "14px" },
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                    {editingId ? "Edit Product" : "Add Product"}
                </DialogTitle>
                <DialogContent dividers>
                    {loadingEdit ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1.5,
                            pt: 0.5,
                        }}
                    >
                        <Box
                            sx={{
                                gridColumn: "1 / -1",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                                p: 1.5,
                                borderRadius: "12px",
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#f8fafc",
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1c2430" }}>
                                Product photos
                            </Typography>
                            <Typography sx={{ mt: -1, fontSize: 12, color: "#64748B" }}>
                                {editingId
                                    ? "Photos already on this product. Use Add Product to upload Hero and Secondary images on create."
                                    : "Add a Hero image and optional Secondary images for marketplace listings."}
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "flex-start",
                                    gap: 1.5,
                                }}
                            >
                                <Box sx={{ minWidth: 160 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: 12, color: "#5c6570", mb: 0.75 }}>
                                        Hero
                                    </Typography>
                                    {heroLocalUrl ? (
                                        <Box sx={{ position: "relative", width: 96, height: 96 }}>
                                            <Box
                                                component="img"
                                                src={heroLocalUrl}
                                                alt="Hero preview"
                                                sx={{
                                                    width: 96,
                                                    height: 96,
                                                    objectFit: "cover",
                                                    borderRadius: "10px",
                                                    border: "1px solid #e2e8f0",
                                                    backgroundColor: "#fff",
                                                }}
                                            />
                                            {!editingId ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={clearHeroSelection}
                                                    sx={{
                                                        position: "absolute",
                                                        top: -8,
                                                        right: -8,
                                                        backgroundColor: "#fff",
                                                        border: "1px solid #e2e8f0",
                                                        "&:hover": { backgroundColor: "#f8fafc" },
                                                    }}
                                                >
                                                    <CloseOutlinedIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            ) : null}
                                        </Box>
                                    ) : heroPreview ? (
                                        <AuthenticatedAssetImage
                                            asset={heroPreview}
                                            alt={form.itemName || "Hero photo"}
                                            sx={{
                                                width: 96,
                                                height: 96,
                                                borderRadius: "10px",
                                                border: "1px solid #e2e8f0",
                                                backgroundColor: "#fff",
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: 96,
                                                height: 96,
                                                borderRadius: "10px",
                                                border: "1px dashed #cbd5e1",
                                                backgroundColor: "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#94a3b8",
                                            }}
                                        >
                                            <ImageOutlinedIcon fontSize="small" />
                                        </Box>
                                    )}
                                    {!editingId ? (
                                        <>
                                            <input
                                                ref={heroInputRef}
                                                type="file"
                                                accept={IMAGE_ACCEPT}
                                                hidden
                                                onChange={onHeroSelected}
                                            />
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                sx={{ ...catalogQuietButtonSx, mt: 1 }}
                                                onClick={() => heroInputRef.current?.click()}
                                            >
                                                {heroLocalUrl ? "Replace hero" : "Upload hero"}
                                            </Button>
                                        </>
                                    ) : null}
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 220 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: 12, color: "#5c6570", mb: 0.75 }}>
                                        Secondary
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                        {existingSupporting.map((asset, index) => (
                                            <AuthenticatedAssetImage
                                                key={asset.gcsObject || asset.sourceUrl || index}
                                                asset={asset}
                                                alt={`Secondary ${index + 1}`}
                                                sx={{
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: "10px",
                                                    border: "1px solid #e2e8f0",
                                                    backgroundColor: "#fff",
                                                }}
                                            />
                                        ))}
                                        {supportingFiles.map((entry) => (
                                            <Box
                                                key={entry.key}
                                                sx={{ position: "relative", width: 72, height: 72 }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={entry.url}
                                                    alt={entry.file.name}
                                                    sx={{
                                                        width: 72,
                                                        height: 72,
                                                        objectFit: "cover",
                                                        borderRadius: "10px",
                                                        border: "1px solid #e2e8f0",
                                                        backgroundColor: "#fff",
                                                    }}
                                                />
                                                {!editingId ? (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeSupportingAt(entry.key)}
                                                        sx={{
                                                            position: "absolute",
                                                            top: -8,
                                                            right: -8,
                                                            backgroundColor: "#fff",
                                                            border: "1px solid #e2e8f0",
                                                            "&:hover": { backgroundColor: "#f8fafc" },
                                                        }}
                                                    >
                                                        <CloseOutlinedIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                ) : null}
                                            </Box>
                                        ))}
                                        {!editingId &&
                                            !supportingFiles.length &&
                                            !existingSupporting.length ? (
                                            <Box
                                                sx={{
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: "10px",
                                                    border: "1px dashed #cbd5e1",
                                                    backgroundColor: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#94a3b8",
                                                }}
                                            >
                                                <ImageOutlinedIcon fontSize="small" />
                                            </Box>
                                        ) : null}
                                    </Box>
                                    {!editingId ? (
                                        <>
                                            <input
                                                ref={supportingInputRef}
                                                type="file"
                                                accept={IMAGE_ACCEPT}
                                                multiple
                                                hidden
                                                onChange={onSupportingSelected}
                                            />
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                sx={{ ...catalogQuietButtonSx, mt: 1 }}
                                                onClick={() => supportingInputRef.current?.click()}
                                            >
                                                Add secondary
                                            </Button>
                                        </>
                                    ) : null}
                                </Box>
                            </Box>
                        </Box>
                        <Typography sx={{ gridColumn: "1 / -1", fontWeight: 700, fontSize: 13, color: "#1c2430" }}>
                            On this SKU
                        </Typography>
                        <Typography sx={{ gridColumn: "1 / -1", mt: -1, fontSize: 12, color: "#64748B" }}>
                            Type a value, or pick a choice. Choose None to clear a select.
                        </Typography>
                        <SkuTextField label="Item Name" value={form.itemName} onChange={setField("itemName")} required />
                        <SkuTextField label="Internal Moniker" value={form.internalMoniker} onChange={setField("internalMoniker")} />
                        <SingleSelectField label="AZ Category" value={form.azCategory} onChange={setSelect("azCategory")} choices={productMeta.azCategory?.choices || []} onManage={() => setOptionEditor({ fieldKey: "azCategory", label: "AZ Category" })} />
                        <SingleSelectField label="eBay Category" value={form.ebayCategory} onChange={setSelect("ebayCategory")} choices={productMeta.ebayCategory?.choices || []} onManage={() => setOptionEditor({ fieldKey: "ebayCategory", label: "eBay Category" })} />
                        <SingleSelectField label="Gender" value={form.gender} onChange={setSelect("gender")} choices={productMeta.gender?.choices || []} onManage={() => setOptionEditor({ fieldKey: "gender", label: "Gender" })} />
                        <SingleSelectField label="Condition Grade" value={form.conditionGrade} onChange={setSelect("conditionGrade")} choices={productMeta.conditionGrade?.choices || []} onManage={() => setOptionEditor({ fieldKey: "conditionGrade", label: "Condition Grade" })} />
                        <SingleSelectField label="Packaging Type" value={form.packagingType} onChange={setSelect("packagingType")} choices={productMeta.packagingType?.choices || []} onManage={() => setOptionEditor({ fieldKey: "packagingType", label: "Packaging Type" })} />
                        <SingleSelectField label="Partial Pack Eligible" value={form.partialPackEligible} onChange={setSelect("partialPackEligible")} choices={productMeta.partialPackEligible?.choices || []} onManage={() => setOptionEditor({ fieldKey: "partialPackEligible", label: "Partial Pack Eligible" })} />
                        <SingleSelectField label="eBay Shipping Policy" value={form.ebayShippingPolicy} onChange={setSelect("ebayShippingPolicy")} choices={productMeta.ebayShippingPolicy?.choices || []} onManage={() => setOptionEditor({ fieldKey: "ebayShippingPolicy", label: "eBay Shipping Policy" })} />
                        <SingleSelectField label="eBay Description Template" value={form.ebayDescriptionTemplate} onChange={setSelect("ebayDescriptionTemplate")} choices={productMeta.ebayDescriptionTemplate?.choices || []} onManage={() => setOptionEditor({ fieldKey: "ebayDescriptionTemplate", label: "eBay Description Template" })} />
                        <SingleSelectField label="Description Template Value" value={form.descriptionTemplateValueEbayConfig} onChange={setSelect("descriptionTemplateValueEbayConfig")} choices={productMeta.descriptionTemplateValueEbayConfig?.choices || []} onManage={() => setOptionEditor({ fieldKey: "descriptionTemplateValueEbayConfig", label: "Description Template Value" })} />
                        <SingleSelectField label="eBay Condition Field" value={form.ebayConditionField} onChange={setSelect("ebayConditionField")} choices={productMeta.ebayConditionField?.choices || []} onManage={() => setOptionEditor({ fieldKey: "ebayConditionField", label: "eBay Condition Field" })} />
                        <SingleSelectField label="eBay Public Interface View" value={form.ebayPublicInterfaceView} onChange={setSelect("ebayPublicInterfaceView")} choices={productMeta.ebayPublicInterfaceView?.choices || []} onManage={() => setOptionEditor({ fieldKey: "ebayPublicInterfaceView", label: "eBay Public Interface View" })} />
                        <SingleSelectField label="Walmart Blocked?" value={form.walmartBlocked} onChange={setSelect("walmartBlocked")} choices={productMeta.walmartBlocked?.choices || []} onManage={() => setOptionEditor({ fieldKey: "walmartBlocked", label: "Walmart Blocked?" })} />
                        <SkuTextField label="Total Quantity" type="number" value={form.totalQuantity} onChange={setField("totalQuantity")} />
                        <SkuTextField label="Inner Packs" type="number" value={form.innerPacks} onChange={setField("innerPacks")} />
                        <SkuTextField label="# of Viable Packs" type="number" value={form.viablePackCount} onChange={setField("viablePackCount")} />
                        <SkuTextField label="UPC" value={form.upc} onChange={setField("upc")} />
                        <SkuTextField label="GTIN" value={form.gtin} onChange={setField("gtin")} />
                        <SkuTextField
                            label="ASIN"
                            value={form.asin}
                            onChange={setField("asin")}
                            hint="Required for Amazon items. Duplicate ASINs are blocked on save."
                        />
                        <SkuTextField
                            label="Amazon URL"
                            value={form.amazonUrl}
                            onChange={setAmazonUrl}
                            hint="If the URL includes /dp/{ASIN}, ASIN is filled automatically when empty."
                        />
                        <SkuTextField label="Walmart URL" value={form.walmartUrl} onChange={setField("walmartUrl")} />
                        <SkuTextField label="MSRP (USD)" type="number" value={form.msrpUsd} onChange={setField("msrpUsd")} />
                        <SkuTextField label="Velocity (Amazon / month)" type="number" value={form.velocityAmazonSalesPerMonth} onChange={setField("velocityAmazonSalesPerMonth")} />
                        <SkuTextField label="Vista Max Price" type="number" value={form.vistaMaxPrice} onChange={setField("vistaMaxPrice")} />
                        <SkuTextField label="Weight (lbs)" type="number" value={form.weightLbs} onChange={setField("weightLbs")} />
                        <SkuTextField label="Height (in)" type="number" value={form.heightIn} onChange={setField("heightIn")} />
                        <SkuTextField label="Width (in)" type="number" value={form.widthIn} onChange={setField("widthIn")} />
                        <SkuTextField label="Depth (in)" type="number" value={form.depthIn} onChange={setField("depthIn")} />
                        <SkuTextField label="Volume Pricing 2x" type="number" value={form.volumePricing2x} onChange={setField("volumePricing2x")} hint="Decimal on the SKU. 0.15 is 15%." />
                        <SkuTextField label="Volume Pricing 3x" type="number" value={form.volumePricing3x} onChange={setField("volumePricing3x")} />
                        <SkuTextField label="Volume Pricing 4x" type="number" value={form.volumePricing4x} onChange={setField("volumePricing4x")} />
                        <FormControlLabel
                            sx={{ alignSelf: "end", m: 0 }}
                            control={
                                <Checkbox
                                    checked={Boolean(form.inspectionWizardBuiltSku)}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            inspectionWizardBuiltSku: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Inspection Wizard Built"
                        />
                        <SkuTextField label="Notes" value={form.notes} onChange={setField("notes")} multiline minRows={2} sx={{ gridColumn: { sm: "1 / -1" } }} />

                        <Typography sx={{ gridColumn: "1 / -1", mt: 1, fontWeight: 700, fontSize: 13, color: "#1c2430" }}>
                            Linked from other tables
                        </Typography>
                        <Typography sx={{ gridColumn: "1 / -1", mt: -1, fontSize: 12, color: "#64748B" }}>
                            Search to link an existing record, or type a new name and choose Create to add it to that table. Clear the field to remove the link.
                        </Typography>
                        <DimensionSingleSelect
                            label="Brand"
                            valueId={form.brand[0] || ""}
                            options={dimensionSeeds.brand}
                            endpoint="broylesllc/brands"
                            onChange={setSingleLink("brand")}
                            hint="Brand table — create saves a new Brand"
                        />
                        <DimensionSingleSelect
                            label="Size"
                            valueId={form.size[0] || ""}
                            options={dimensionSeeds.size}
                            endpoint="broylesllc/sizes"
                            onChange={setSingleLink("size")}
                            hint="Size table — create saves a new Size"
                        />
                        <DimensionSingleSelect
                            label="Color / Flavor / Variety"
                            valueId={form.colorFlavor[0] || ""}
                            options={dimensionSeeds.colorFlavor}
                            endpoint="broylesllc/color-flavors"
                            onChange={setSingleLink("colorFlavor")}
                            hint="Color / flavor table — create saves a new record"
                        />
                        <DimensionSingleSelect
                            label="Model"
                            valueId={form.model[0] || ""}
                            options={dimensionSeeds.model}
                            endpoint="broylesllc/models"
                            onChange={setSingleLink("model")}
                            hint="Model table — create saves a new Model"
                        />
                        <DimensionMultiSelect
                            label="Status Checks"
                            valueIds={form.statusChecks}
                            options={dimensionSeeds.statusChecks}
                            endpoint="broylesllc/status-checks"
                            onChange={setSelect("statusChecks")}
                            hint="Status Checks table — create saves a new Status Check"
                        />
                    </Box>
                    <Typography sx={{ mt: 2.5, mb: 1, fontWeight: 700, fontSize: 13, color: "#64748B" }}>
                        Lookups and formulas
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1.5,
                        }}
                    >
                        <LookupField label="Listing Field" value={computed.listingField} />
                        <LookupField label="Internal SKU ID" value={computed.internalSkuId} />
                        <LookupField label="WPID" value={computed.wpid} />
                        <LookupField label="MAC Max Price" value={computed.macMaxPrice} />
                        <LookupField label="BidFTA Max Price" value={computed.bidftaMaxPrice} />
                        <LookupField label="F+F Pricing" value={computed.ffPricing} />
                        <LookupField label="FB MKT Pricing" value={computed.fbMktPricing} />
                        <LookupField label="Purchase Price (eBay)" value={computed.purchasePriceEbay} />
                        <LookupField label="Est. Shipping Cost" value={computed.estShippingCost} />
                        <LookupField label="Cost Rollup 1x" value={computed.costRollup1x} source="Shipping class table" />
                        <LookupField label="Cost Rollup 2x" value={computed.costRollup2x} source="Shipping class table" />
                        <LookupField label="Cost Rollup 3x" value={computed.costRollup3x} source="Shipping class table" />
                        <LookupField label="Cost Rollup 4x" value={computed.costRollup4x} source="Shipping class table" />
                        <LookupField label="Suggested eBay Price" value={computed.suggestedEbayPrice} />
                        <LookupField label="Suggested Walmart Price" value={computed.suggestedWalmartPrice} />
                        <LookupField label="Suggested Margin" value={computed.suggestedMargin} />
                        <LookupField label="Vista Pack Price" value={computed.vistaPackPrice} />
                        <LookupField label="Helper Flag" value={computed.helperFlagWalmartAutomation} />
                        <LookupField label="Listed On" value={computed.listedPlatforms} />
                        {computed.ebayNeedsMigrate ? (
                            <LookupField
                                label="eBay Migrate"
                                value={computed.ebayNeedsMigrate}
                            />
                        ) : null}
                        {Array.isArray(computed.quantitySyncErrors) &&
                        computed.quantitySyncErrors.some((e) => e && e.error !== false) ? (
                            <LookupField
                                label="Platform Qty Errors"
                                value={computed.quantitySyncErrors
                                    .filter((e) => e && e.error !== false)
                                    .map((e) => e.platform || "Platform")
                                    .join(", ")}
                            />
                        ) : null}
                        <LookupField label="Walmart SKU" value={form.walmartLinkedSku} source="Upload Products" />
                        <LookupField label="eBay SKU" value={form.ebayLinkedSku} source="Upload Products" />
                        <LookupField label="Walmart Price" value={moneyText(form.walmartPrice)} source="Upload Products" />
                        <LookupField label="eBay Price" value={moneyText(form.ebayPrice)} source="Upload Products" />
                        <LookupField label="Positive Examples" value={computed.positiveExampleCount} source="Status Checks table" />
                        <LookupField label="Negative Examples" value={computed.negativeExampleCount} source="Status Checks table" />
                        <LookupField label="Available Qty eBay" value={computed.availableQuantityEbay} />
                        <LookupField label="Available Qty Walmart" value={computed.availableQuantityWalmart} />
                        <LookupField label="eBay Quant 30+ Days" value={computed.ebayQuant30PlusDays} />
                        <LookupField label="WM Quant 30+ Days" value={computed.walmartQuant30PlusDays} />
                        <LookupField
                            label="eBay Older Listing Age"
                            value={computed.ebayOlderListingAge}
                            source="Days since Date Listed"
                        />
                        <LookupField
                            label="WM Older Listing Age"
                            value={computed.walmartOlderListingAge}
                            source="Days since Date Listed"
                        />
                        <LookupField label="Total Quant on Hand" value={computed.totalQuantOnHand} />
                        <LookupField
                            label="Purchase Date Rollup"
                            value={computed.oldestPurchaseDate}
                            source="Oldest purchase date in inventory"
                        />
                        <LookupField
                            label="Expiration Date Rollup"
                            value={computed.expirationDateRollup}
                            source="Oldest expiration among Listed units"
                        />
                    </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={handleClose} sx={catalogQuietButtonSx}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving || loadingEdit}
                        onClick={save}
                        startIcon={
                            saving ? <CircularProgress size={14} color="inherit" /> : null
                        }
                        sx={catalogPrimaryButtonSx}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <SelectOptionsDialog
                open={Boolean(optionEditor)}
                fieldKey={optionEditor?.fieldKey || ""}
                fieldLabel={optionEditor?.label || ""}
                onClose={() => setOptionEditor(null)}
                onChanged={(change) => {
                    refetchFieldMeta();
                    if (!optionEditor?.fieldKey) return;
                    const key = optionEditor.fieldKey;
                    setForm((prev) => {
                        if (change.type === "rename" && prev[key] === change.previous) {
                            return { ...prev, [key]: change.label };
                        }
                        if (change.type === "remove" && prev[key] === change.label) {
                            return { ...prev, [key]: "" };
                        }
                        if (change.type === "add" && !prev[key]) {
                            return { ...prev, [key]: change.label };
                        }
                        return prev;
                    });
                }}
            />
        </>
    );
}
